import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const RecordAudits = () => (
  <Page
    title="Record Audits"
    intro="Record audits are quality reviews of submitted patient records. Records matching your organisation's audit criteria are flagged automatically at submission, the Governance team completes the audit against a standard question set, and the clinician sees the outcome and any feedback on their own My Audits page."
  >
    <Section title="Who can do this?">
      <p>
        Auditing is a governance function: the audit queue and the audit
        itself are only served to members of the <strong>Governance</strong>{' '}
        team, behind a multi-factor authentication (MFA) check. Every signed-in
        user can see the completed audits of records they were involved in on{' '}
        <DocLink url="/my-audits" text="My Audits" />.
      </p>
    </Section>

    <Section title="How records are flagged for audit">
      <p>
        When a record is submitted it is checked against the audit criteria in{' '}
        <strong>Site Configuration → Patient Records</strong>. A record can be
        flagged for one or more reasons:
      </p>
      <Bullets>
        <Bullet>
          <strong>All records audited</strong> — every submitted record is
          flagged.
        </Bullet>
        <Bullet>
          <strong>Specific presenting complaints</strong> — the record&apos;s
          presenting complaint matches one your organisation chose to audit.
        </Bullet>
        <Bullet>
          <strong>New starter involved</strong> — a crew member on the record
          still has New Starter status.
        </Bullet>
        <Bullet>
          <strong>Paediatric patient</strong> — the patient was under 18 at
          the incident (from the date of birth, or the &quot;under 18&quot;
          flag when the DOB is unknown).
        </Bullet>
        <Bullet>
          <strong>Discharged on scene</strong> — the patient was discharged
          following treatment or with advice only.
        </Bullet>
        <Bullet>
          <strong>Transferred to hospital</strong> — the patient was conveyed
          or handed to an ambulance crew.
        </Bullet>
        <Bullet>
          <strong>Drug administration</strong> — a tracked medicine was
          administered on the record.
        </Bullet>
        <Bullet>
          <strong>Random sample</strong> — records matching none of the above
          can still be sampled at a configurable monthly percentage (10% by
          default).
        </Bullet>
      </Bullets>
      <Info title="Welfare records are never audited">
        Welfare records are non-clinical logs, so they are excluded from
        audit selection entirely. Resubmitting a record (for example after an
        ownership transfer) never creates a second audit — newly matched
        reasons are merged into the existing one.
      </Info>
    </Section>

    <Section title="Critical care case review">
      <p>
        Organisations with the Critical Care record type enabled also get
        automatic <strong>clinical case review</strong> flags, independent of
        the sampling criteria above. Records whose live-console or treatment
        entries show events that always warrant review — a surgical airway,
        more than one intubation attempt, a failed airway, REBOA,
        resuscitative thoracotomy, a sedation adverse event, and similar —
        are flagged with a &quot;Critical care review&quot; reason. This
        applies to any record type: a full ePCR with a surgical airway is
        reviewed just like a Critical Care record. Automatic case review can
        be switched off in the Critical Care settings card.
      </p>
    </Section>

    <Section title="Raising an audit by hand">
      <p>
        Governance can also audit any submitted record on request, in two
        places:
      </p>
      <Bullets>
        <Bullet>
          <strong>Create Audit</strong> in the record header menu on the
          report view — see{' '}
          <DocLink
            docsPage
            url="/clinical-records/reviewing-and-managing"
            text="Reviewing and Managing Patient Report Forms"
          />
          .
        </Bullet>
        <Bullet>
          <strong>Raise Audit</strong> on{' '}
          <DocLink url="/patient-records/audits" text="Record Audits" /> —
          type the ePCR number, confirm the matched record, and optionally
          add a reason and name the clinician the audit is about (useful when
          a clerk started the record for the treating clinician).
        </Bullet>
      </Bullets>
      <p>
        Only submitted records can be audited — a draft can still change under
        the auditor. Each record has one audit: raising one on a record
        already awaiting audit just adds the manual reason, and a record whose
        audit is already completed cannot be re-raised. Manually raised
        audits are written to the audit trail.
      </p>
    </Section>

    <Section title="Completing an audit">
      <Step number={1} title="Work the Awaiting Audit queue">
        <DocLink url="/patient-records/audits" text="Record Audits" /> shows
        stat tiles (Awaiting Audit, Completed This Month, Compliance Rate, Not
        Compliant) above tabs for records awaiting audit and completed audits.
        Each row shows the record, the clinician being audited, and the audit
        reasons.
      </Step>
      <Screenshot url="patient-records/record-audits.png">
        The Record Audits page with its stat tiles above the Awaiting Audit
        queue.
      </Screenshot>
      <Step number={2} title="Open the audit and the record">
        The audit dialog links to the full report view (opening it records an
        access reason as usual) and lists the standard audit criteria —
        history and presenting complaint, assessment and observations,
        treatment, medicines, outcome and disposition, and overall record
        completeness. Mark each one Compliant or Not Compliant, with an
        optional comment per criterion.
      </Step>
      <Step number={3} title="Record the outcome and feedback">
        The overall result is Compliant only when every criterion passes. Any
        feedback written for the clinician is shown to them with the outcome.
        Governance can also change which clinician the audit is about —
        normally whoever submitted the record, falling back to the current
        owner and then the author.
      </Step>
      <Warning title="Self-audit is blocked by default">
        An auditor who took part in the record — as its author, a past owner,
        or listed crew — cannot complete its audit while{' '}
        <strong>Prevent self-audit</strong> is on (the default): another
        member of the Governance team must do it. The queue marks these rows
        &quot;You&apos;re involved&quot;.
      </Warning>
    </Section>

    <Section title="The clinician's view: My Audits">
      <Bullets>
        <Bullet>
          <DocLink url="/my-audits" text="My Audits" /> (also in the account
          menu) lists completed audits of records the signed-in user took part
          in — the outcome per criterion, the overall result, and any
          feedback. Audits still awaiting completion stay internal to the
          Governance team.
        </Bullet>
        <Bullet>
          The clinician an audit is about is asked to{' '}
          <strong>mark it reviewed</strong>, clearing it from their to-do
          list — unreviewed outcomes appear on their dashboard to-do list.
        </Bullet>
        <Bullet>
          Governance team members see an <strong>ePCRs Awaiting Audit</strong>{' '}
          card on their home dashboard whenever flagged records are waiting;
          it opens the Record Audits queue.
        </Bullet>
      </Bullets>
    </Section>
  </Page>
)

export default RecordAudits
